<script setup>
import "vue3-carousel/carousel.css";
import { Carousel, Slide, Navigation, Pagination } from "vue3-carousel";
import { computed, ref } from "vue";

const props = defineProps({
    // Prefer `galery` to match HomeController/Home.vue naming
    galery: { type: [Array, Object], default: () => [] },
    // Backward compatibility: still accept `images`
    images: { type: Array, default: () => [] },
    autoplay: { type: [Number, Boolean], default: 4000 },
    wrap: { type: Boolean, default: true },
    showThumbs: { type: Boolean, default: true },
    sectionTitle: { type: String, default: 'Galeri' },
    // When true: show all images (gallery). When false: only first image (cover).
    galleryMode: { type: Boolean, default: false },
});

const current = ref(0);

// Normalize input: support multiple shapes and optionally limit to first image (cover)
const items = computed(() => {
    const g = props.galery;
    // If array directly
    if (Array.isArray(g)) return props.galleryMode ? g : (g.length ? [g[0]] : []);
    if (g && Array.isArray(g.data)) {
        const first = g.data[0] || {};
        if (Array.isArray(first.image_path)) {
            if (props.galleryMode) return first.image_path;
            const v = first.image_path[0];
            return v ? [v] : [];
        }
    }
    if (g && Array.isArray(g.image_path)) {
        if (props.galleryMode) return g.image_path;
        const v = g.image_path[0];
        return v ? [v] : [];
    }
    if (Array.isArray(props.images)) return props.galleryMode ? props.images : (props.images.length ? [props.images[0]] : []);
    return [];
});
const hasItems = computed(() => Array.isArray(items.value) && items.value.length > 0);

// Resolve gallery meta
const galleryTitle = computed(() => {
    const g = props.galery;
    if (g && Array.isArray(g.data) && g.data[0]) return g.data[0].title || '';
    if (g && typeof g.title === 'string') return g.title;
    return '';
});
const galleryDescription = computed(() => {
    const g = props.galery;
    if (g && Array.isArray(g.data) && g.data[0]) return g.data[0].description || '';
    if (g && typeof g.description === 'string') return g.description;
    return '';
});

const settingsMain = computed(() => ({
    itemsToShow: 1,
    snapAlign: 'center',
    autoplay: props.autoplay || 0,
    pauseAutoplayOnHover: true,
    wrapAround: props.wrap,
    transition: 500,
}));

const thumbsSettings = computed(() => ({
    itemsToShow: 6,
    snapAlign: 'start',
    breakpoints: {
        1280: { itemsToShow: 6 },
        1024: { itemsToShow: 5 },
        768: { itemsToShow: 4 },
        640: { itemsToShow: 4 },
        0: { itemsToShow: 3 },
    },
}));

const goTo = (idx) => (current.value = idx);
</script>

<template>
    <div class="gallery-wrapper">
        <div v-if="hasItems" class="overflow-hidden bg-white border border-gray-200 shadow-xl">
            <div class="px-5 py-3 border-b border-gray-100 bg-gradient-to-r from-blue-600 to-blue-500">
                <h2 class="text-sm font-semibold tracking-wide text-white">{{ sectionTitle }}</h2>
            </div>
            <div class="p-4 space-y-3">
                <!-- Main carousel -->
                <Carousel v-model="current" v-bind="settingsMain" class="gallery-main">
                    <Slide v-for="(img, i) in items" :key="i">
                        <div class="relative w-full h-full gallery__item">
                            <img :src="img.src || img.url || img" :alt="img.alt || img.caption || `Gambar ${i + 1}`"
                                loading="lazy" class="object-cover object-center w-full h-full" />
                            <div v-if="img.caption"
                                class="absolute bottom-0 left-0 right-0 p-3 text-white bg-gradient-to-t from-black/70 to-black/0">
                                <p class="text-sm text-center md:text-base">{{ img.caption }}</p>
                            </div>
                        </div>
                    </Slide>
                    <template #addons>
                        <div class="flex items-center justify-between px-3 py-2">
                            <Navigation />
                            <Pagination />
                        </div>
                    </template>
                </Carousel>

                <!-- Title and description under the image -->
                <div v-if="galleryTitle" class="px-1">
                    <h3 class="mt-1 text-lg font-semibold text-gray-900">{{ galleryTitle }}</h3>
                    <p v-if="galleryDescription" class="mt-1 text-sm text-gray-600">{{ galleryDescription }}</p>
                </div>

                <!-- Thumbnails -->
                <div v-if="showThumbs && galleryMode && items.length > 1" class="thumbs">
                    <Carousel v-model="current" v-bind="thumbsSettings" :wrapAround="false" :mouseDrag="true"
                        :touchDrag="true" :transition="300" class="thumbs-carousel">
                        <Slide v-for="(img, i) in items" :key="`thumb-${i}`">
                            <button type="button" class="thumb" :class="{ active: current === i }" @click="goTo(i)">
                                <img :src="img.src || img.url || img" loading="lazy"
                                    :alt="img.alt || img.caption || `Thumbnail ${i + 1}`" />
                            </button>
                        </Slide>
                    </Carousel>
                </div>
            </div>
        </div>

        <div v-else class="empty">
            <div class="placeholder">
                <span>Tidak ada gambar</span>
            </div>
        </div>
    </div>
</template>

<style scoped>
.gallery__item {
    position: relative;
    width: 100%;
    /* Ideal, responsif */
    height: clamp(300px, 55vh, 540px);
    margin: 0;
    padding: 0;
    overflow: hidden;
}

@media (max-width: 1024px) {
    .gallery__item {
        height: clamp(280px, 48vh, 460px);
    }
}

@media (max-width: 768px) {
    .gallery__item {
        height: clamp(240px, 44vh, 360px);
    }
}

@media (max-width: 480px) {
    .gallery__item {
        height: clamp(200px, 40vh, 300px);
    }
}

.gallery__item img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 0;
}

/* Navigation buttons: square, subtle */
.carousel__prev,
.carousel__next {
    background-color: #ffffff;
    border-radius: 0;
    color: #111827;
    font-size: 1.1rem;
    padding: 6px;
    z-index: 10;
    box-shadow: 0 6px 18px rgba(0, 0, 0, .15);
}

.carousel__prev:hover,
.carousel__next:hover {
    background-color: #f3f4f6;
}

/* Pagination pills aligned right */
.carousel__pagination {
    padding: 0;
    display: flex;
    justify-content: flex-end;
    gap: 6px;
}

.carousel__pagination-button::after {
    background-color: #e5e7eb;
    width: 18px;
    height: 4px;
    border-radius: 0;
}

.carousel__pagination-button--active::after {
    background-color: #2563eb;
}

/* Thumbnails */
.thumbs {
    margin-top: .25rem;
}

.thumbs-carousel {
    --thumb-h: 72px;
}

.thumb {
    display: block;
    width: 100%;
    height: var(--thumb-h);
    padding: 2px;
    border: 1px solid #e5e7eb;
    background: #fff;
    cursor: pointer;
}

.thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.thumb.active {
    outline: 2px solid #2563eb;
    outline-offset: -2px;
}

@media (max-width: 768px) {
    .thumbs-carousel {
        --thumb-h: 64px;
    }
}

@media (max-width: 480px) {
    .thumbs-carousel {
        --thumb-h: 56px;
    }
}

/* Empty state */
.empty .placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    height: clamp(200px, 40vh, 320px);
    background: repeating-linear-gradient(45deg, #f9fafb, #f9fafb 10px, #f3f4f6 10px, #f3f4f6 20px);
    color: #6b7280;
    font-size: .95rem;
}
</style>
